/**
 * One live feed of the agent's own WFM state (GET /wfm/me/adherence) shared
 * by everything that shows it — the BreakAssistant popups, the My Schedule
 * "now" panel and the week list — so there is a single poller however many
 * are mounted.
 *
 * Refreshes every 30 s while anything is subscribed (10 s while a break is
 * pending on a contact), and ~1.5 s after every
 * live agent status change (socket-fed agentStore), which is what makes a
 * break deferred by a call snap to its new start as soon as the call ends.
 */
import { useEffect } from 'react'
import { create } from 'zustand'
import { api } from '../../api/client.js'
import { useAgentStore } from '../../store/agentStore.js'
import type { DayActivity } from './wfm-shared.js'

export interface LiveBlock {
  start: string
  end: string
  activity: string
  category: string
  color: string | null
  allowedStates: string[]
  /** Set when a contact pushed this break back. */
  scheduledStart?: string
  deferredMin?: number
  /** The contact is still running — the break starts when it ends. */
  pending?: boolean
}

export interface MeAdherence {
  targetPct: number
  graceMin: number
  currentState: string
  stateSince: string | null
  inAdherence: boolean | null
  shift: { start: string; end: string } | null
  punctuality: { status: 'early' | 'on_time' | 'late' | 'not_logged_in' | 'upcoming'; minutes: number; at: string | null } | null
  session: { loginAt: string | null; online: boolean; lastLogoutAt: string | null }
  today: {
    adherencePct: number | null
    conformancePct: number | null
    scheduledMin: number
    outOfAdherenceMin: number
    exceptions: { start: string; end: string; minutes: number; expected: string; actual: string }[]
    blocks: LiveBlock[]
  } | null
  nextShift: { startAt: string; endAt: string } | null
  activity: DayActivity | null
}

/**
 * The browser softphone's call state, mirrored from AgentLayout. Calls dialled
 * from the softphone never move the agent's server status to IN_CALL, so WFM
 * views use this to show "On a call" live (the CDR covers it afterwards).
 */
interface PhoneCallState {
  state: string
  /** When the current call connected (ms), null when idle. */
  since: number | null
  set: (state: string) => void
}
export const usePhoneCall = create<PhoneCallState>()((set, get) => ({
  state: 'idle',
  since: null,
  set: (state) => {
    const wasOn = get().state === 'in_call' || get().state === 'on_hold'
    const isOn = state === 'in_call' || state === 'on_hold'
    set({ state, since: isOn ? (wasOn ? get().since : Date.now()) : null })
  },
}))

interface LiveState {
  data: MeAdherence | null
  error: string | null
  /** 404 NO_AGENT — the user has no agent row; stop polling. */
  unavailable: boolean
  tz: string
  load: () => void
}

export const useWfmLive = create<LiveState>()((set, get) => ({
  data: null,
  error: null,
  unavailable: false,
  tz: 'UTC',
  load: () => {
    if (get().unavailable) return
    api.get<MeAdherence>(`/wfm/me/adherence?timezone=${encodeURIComponent(get().tz)}`)
      .then((data) => set({ data, error: null }))
      .catch((err: unknown) => {
        const e = err as { status?: number; body?: { error?: string; message?: string } }
        set({ error: e?.body?.message ?? 'Adherence unavailable', unavailable: e?.status === 404 || e?.body?.error === 'NO_AGENT' })
      })
  },
}))

let subscribers = 0
let timer: ReturnType<typeof setInterval> | null = null
let unsubStatus: (() => void) | null = null
let statusTimer: ReturnType<typeof setTimeout> | null = null

function start(): void {
  const { load } = useWfmLive.getState()
  load()
  // 10 s while a break is waiting on a contact (it keeps sliding), else 30 s.
  let lastLoad = Date.now()
  timer = setInterval(() => {
    const pending = useWfmLive.getState().data?.today?.blocks.some((b) => b.pending) ?? false
    if (pending || Date.now() - lastLoad >= 30_000) {
      lastLoad = Date.now()
      useWfmLive.getState().load()
    }
  }, 10_000)
  let last = useAgentStore.getState().status
  const unsubAgent = useAgentStore.subscribe((s) => {
    if (s.status === last) return
    last = s.status
    if (statusTimer) clearTimeout(statusTimer)
    statusTimer = setTimeout(() => useWfmLive.getState().load(), 1500)
  })
  // A softphone call just ended: its CDR row lands at hangup — refresh shortly after.
  let lastPhone = usePhoneCall.getState().since
  const unsubPhone = usePhoneCall.subscribe((p) => {
    if (lastPhone !== null && p.since === null) {
      if (statusTimer) clearTimeout(statusTimer)
      statusTimer = setTimeout(() => useWfmLive.getState().load(), 4000)
    }
    lastPhone = p.since
  })
  unsubStatus = () => { unsubAgent(); unsubPhone() }
}

function stop(): void {
  if (timer) clearInterval(timer)
  if (statusTimer) clearTimeout(statusTimer)
  unsubStatus?.()
  timer = null
  statusTimer = null
  unsubStatus = null
}

/** Subscribe to the shared feed; the first subscriber starts it, the last stops it. */
export function useWfmLiveFeed(tz: string): { data: MeAdherence | null; error: string | null; unavailable: boolean; reload: () => void } {
  const data = useWfmLive((s) => s.data)
  const error = useWfmLive((s) => s.error)
  const unavailable = useWfmLive((s) => s.unavailable)
  useEffect(() => {
    if (useWfmLive.getState().tz !== tz) useWfmLive.setState({ tz })
    subscribers++
    if (subscribers === 1) start()
    return () => {
      subscribers--
      if (subscribers === 0) stop()
    }
  }, [tz])
  return { data, error, unavailable, reload: useWfmLive.getState().load }
}
